import { useEffect, useRef, useState, useSyncExternalStore, type KeyboardEvent, type ReactNode } from 'react'
import { ArrowUp, ChevronDown, FileText, Mail, Phone } from 'lucide-react'
import { Link } from 'react-router-dom'
import { getActivePod } from '@/lib/pod'
import { cn } from '@/utils/cn'
import { sendChat, openableDocuments, type ChatReply, type ChatTurn, type Source } from '@/lib/api'
import { createConversationStore, type ConversationStore } from '@/lib/conversation'

/**
 * Phase 2 chat interface — the pod-branded assistant surface.
 *
 * Talks to the engine via the multi-turn /api/chat contract. All colour comes
 * from the pod's CSS-variable tokens; ALL words a visitor reads (brand, role
 * label, welcome intro, starter questions, composer prompt, footer note) come
 * from the active pod. Engine-clean: this component contains no niche or brand
 * string of any pod — the Batch B fix for the hardcoded-Oxydise-strings issue.
 */

/**
 * OPTIONAL PROPS (Kelvo build step 2, item 5). The public chat mounts this with
 * none and is unchanged: the defaults ARE the public behaviour. The Kelvo staff
 * chat (src/routes/StaffChat.tsx) reuses this exact screen — same composer,
 * same formatting, same accessibility — and supplies only what differs:
 *
 *   transport       how a turn reaches the engine. Public: sendChat (no login,
 *                   pod in the body). Staff: sendStaffChat with a Clerk token
 *                   and NO pod (the Worker takes it from the session).
 *   headerAction    one element at the right of the header — the staff chat's
 *                   account button (sign out).
 *   headerNav       the owner's page links (Documents, Facts, Questions). On a
 *                   tablet or wider they sit inline, before headerAction. On a
 *                   phone they take a row of their own under the header, which
 *                   scrolls sideways rather than squeezing anything (A26).
 *   showPrivacyLink the footer link to /privacy, which is the PUBLIC chat's
 *                   notice. Staff are covered by their employer's own notice;
 *                   linking them to a buyer-facing page would be wrong.
 *
 * One screen, two doors, is the same rule as one answer path, two channels:
 * a second copy of this component would drift from the first one fix at a time.
 */
interface AppProps {
  transport?: (history: ChatTurn[]) => Promise<ChatReply>
  headerAction?: ReactNode
  /**
   * A26 — WHY THE LINKS ARE NOT PART OF headerAction ANY MORE. headerAction is
   * `shrink-0`: it never gives up width. With three links and the account
   * button in it, a phone had less room than the group needed, so the name
   * block was squeezed to nothing, the logo was drawn under "Documents", and
   * the account button was pushed off the screen (live, 29 Sep 2026). Every
   * test was green: they checked the links existed, not that they fitted.
   *
   * Given, the header wraps: row one keeps the logo, the name and headerAction;
   * the nav is `basis-full` and ordered last below `sm`, so it always gets a row
   * of its own, and scrolls sideways if it is ever wider than the phone (a
   * fourth link, the renewals dashboard, is agreed). From `sm` it returns to the
   * single row. Absent (the public chat, every Foredesk site, and a Kelvo
   * account that is not the owner's), the header is byte-for-byte what it was.
   */
  headerNav?: ReactNode
  showPrivacyLink?: boolean
  /**
   * Kelvo build step 3c — open the stored original behind an answer. Given only
   * by the staff chat. Absent (the public chat, every Foredesk site) means no
   * document buttons are ever drawn, whatever a reply contains.
   */
  openDocument?: (documentId: string, name: string) => Promise<void>
  /**
   * Batch 2v — where the conversation lives. Given only by the staff chat: a
   * store that outlives this page, keyed to the signed-in person, so leaving
   * for Questions, Documents or Facts and coming back keeps the conversation
   * (src/lib/conversation.ts). Absent (the public chat, every Foredesk site),
   * App makes its own store that dies with the page: the behaviour it had.
   */
  conversation?: ConversationStore
}

export default function App({
  transport = sendChat,
  headerAction,
  headerNav,
  showPrivacyLink = true,
  openDocument,
  conversation,
}: AppProps = {}) {
  const pod = getActivePod()
  const [ownConversation] = useState(createConversationStore)
  const store = conversation ?? ownConversation
  const { messages, status, error, input } = useSyncExternalStore(store.subscribe, store.getState)
  const setInput = store.setInput

  // Coming back to a conversation that already has messages (the staff chat,
  // after Questions or Documents): the first scroll jumps straight to the end
  // instead of gliding down the whole thread. A fresh chat never takes this
  // path, so the public chat scrolls exactly as before.
  const restoredRef = useRef(messages.length > 0)

  const scrollRef = useRef<HTMLElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // AUTO-SCROLL — CONTAINER ONLY, NEVER scrollIntoView.
  //
  // This previously called bottomRef.current.scrollIntoView(). That method walks
  // UP the frame tree: it scrolls every scrollable ancestor needed to bring the
  // element into view, and inside an iframe those ancestors include the HOST
  // PAGE. Every pod is embedded as an iframe on a client site, so each reply
  // yanked the client's whole page down and the visitor had to scroll back up to
  // read the answer. Reported live on cornwallplaysafety.co.uk, 14 Aug 2026.
  //
  // Setting scrollTop on our own overflow container cannot touch the parent
  // document — the effect is confined to the iframe by construction, not by a
  // flag that a browser might interpret differently. The same rule applies to
  // any future focus() call on the composer: focusing an element in an iframe
  // also scrolls the host page, so the composer is deliberately NOT re-focused
  // after send.
  useEffect(() => {
    if (messages.length === 0) return
    const el = scrollRef.current
    if (!el) return
    const behavior = restoredRef.current ? 'auto' : 'smooth'
    restoredRef.current = false
    el.scrollTo({ top: el.scrollHeight, behavior })
  }, [messages, status])

  // A half-typed question that survived the trip away gets its height back.
  useEffect(() => {
    if (input) onInput()
    // Once, on arrival: afterwards onInput runs from the textarea itself.
  }, [])

  // The turn is sent BY THE STORE and its reply written INTO the store, so a
  // reply that lands after this page has gone is kept, not lost.
  function send(text: string) {
    if (store.send(text, transport)) resetTextareaHeight()
  }

  function retry() {
    store.retry(transport)
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      send(input)
    }
  }

  function resetTextareaHeight() {
    const el = textareaRef.current
    if (el) el.style.height = 'auto'
  }

  function onInput() {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`
  }

  const isEmpty = messages.length === 0

  // MOBILE VIEWPORT HEIGHT. h-screen is 100vh, which mobile browsers measure
  // against the viewport as if the URL bar were hidden. On first paint the
  // composer therefore sits just below the fold — the visitor opens the page and
  // cannot see the message box until a scroll or resize recalculates it. h-[100dvh]
  // tracks the ACTUAL visible viewport and updates as the browser chrome shows and
  // hides. BOTH classes are kept deliberately: dvh wins wherever it is supported,
  // and 100vh remains the fallback for older browsers that do not know dvh.
  return (
    <div className="flex flex-col h-[100svh]">
      <a
        href="#composer"
        className="sr-only focus:not-sr-only focus:absolute focus:top-3 focus:left-3 focus:z-10 focus:bg-surface-raised focus:px-3 focus:py-2 focus:rounded focus-visible:ring-2 focus-visible:ring-brand-accent"
      >
        Skip to message box
      </a>

      {/* Header */}
      <header className="shrink-0 border-b border-border-subtle bg-surface-raised">
        <div
          className={
            headerNav
              ? 'mx-auto max-w-3xl w-full px-4 py-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-2'
              : 'mx-auto max-w-3xl w-full px-4 py-3 flex items-center justify-between gap-4'
          }
        >
          <div className={headerNav ? 'flex flex-1 items-center gap-3 min-w-0' : 'flex items-center gap-3 min-w-0'}>
            <OxyMark />
            <div className="min-w-0">
              <p className="font-display font-semibold leading-tight tracking-tight truncate">
                {pod.assistantName}
              </p>
              <p className="text-xs text-text-muted leading-tight truncate">
                {pod.brandName} · {pod.roleLabel}
              </p>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-4 text-xs text-text-muted">
            {pod.contact.email && (
              <a
                href={`mailto:${pod.contact.email}`}
                className="flex items-center gap-1.5 hover:text-brand-primary focus-visible:ring-2 focus-visible:ring-brand-accent rounded"
              >
                <Mail className="w-3.5 h-3.5" aria-hidden="true" />
                {pod.contact.email}
              </a>
            )}
            {pod.contact.phone && (
              <a
                href={`tel:${pod.contact.phone.replace(/\s/g, '')}`}
                className="flex items-center gap-1.5 hover:text-brand-primary focus-visible:ring-2 focus-visible:ring-brand-accent rounded"
              >
                <Phone className="w-3.5 h-3.5" aria-hidden="true" />
                {pod.contact.phone}
              </a>
            )}
          </div>
          {headerNav && (
            <nav
              aria-label="Your pages"
              className="order-last basis-full -mx-1 overflow-x-auto px-1 py-1 sm:order-none sm:basis-auto sm:shrink-0"
            >
              <div className="flex w-max items-center gap-2 sm:gap-3">{headerNav}</div>
            </nav>
          )}
          {headerAction && <div className="shrink-0">{headerAction}</div>}
        </div>
      </header>

      {/* Conversation */}
      <main ref={scrollRef} className="flex-1 overflow-y-auto">
        <div
          className="mx-auto max-w-3xl w-full px-4 py-6"
          role="log"
          aria-live="polite"
          aria-label={`Conversation with ${pod.assistantName}`}
        >
          {isEmpty ? (
            <EmptyState
              assistantName={pod.assistantName}
              tagline={pod.tagline}
              welcomeIntro={pod.welcomeIntro}
              starters={pod.starters}
              onPick={send}
              disabled={status === 'sending'}
            />
          ) : (
            <ul className="stack-y-5">
              {messages.map((m) =>
                m.role === 'user' ? (
                  <li key={m.id} className="flex justify-end">
                    <div className="max-w-[80%] rounded-card bg-brand-primary px-4 py-2.5 text-white">
                      <p className="whitespace-pre-wrap">{m.content}</p>
                    </div>
                  </li>
                ) : (
                  <li key={m.id} className="flex gap-3">
                    <OxyMark small />
                    <div className="max-w-[85%] min-w-0">
                      <div className="rounded-card border border-border-subtle bg-surface-raised px-4 py-3">
                        <FormattedMessage text={m.content} />
                      </div>
                      {openDocument && m.sources && (
                        <OriginalDocuments sources={m.sources} open={openDocument} />
                      )}
                      {m.sources && m.sources.length > 0 && <Sources sources={m.sources} />}
                    </div>
                  </li>
                ),
              )}
              {status === 'sending' && (
                <li className="flex gap-3" aria-hidden="true">
                  <OxyMark small />
                  <Thinking />
                </li>
              )}
            </ul>
          )}
          {status === 'sending' && <span className="sr-only">{pod.assistantName} is thinking.</span>}
        </div>
      </main>

      {/* Composer */}
      <div id="composer" className="shrink-0 border-t border-border-subtle bg-surface-raised">
        <div className="mx-auto max-w-3xl w-full px-4 py-3">
          {error && (
            <div className="mb-2 flex items-center justify-between gap-3 rounded-lg border border-[rgb(var(--color-text-danger)/0.3)] bg-surface-subtle px-3 py-2 text-sm text-text-danger">
              <span>{error}</span>
              <button
                onClick={retry}
                className="shrink-0 font-medium underline hover:no-underline focus-visible:ring-2 focus-visible:ring-brand-accent rounded"
              >
                Try again
              </button>
            </div>
          )}
          <div className="flex items-end gap-2 rounded-card border border-border-subtle bg-surface-raised px-3 py-2 focus-within:border-brand-accent">
            <label htmlFor="message" className="sr-only">
              {pod.inputPrompt}
            </label>
            <textarea
              id="message"
              ref={textareaRef}
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onInput={onInput}
              onKeyDown={onKeyDown}
              placeholder={`${pod.inputPrompt}…`}
              disabled={status === 'sending'}
              className="flex-1 resize-none bg-transparent py-1.5 text-text-primary placeholder:text-text-muted focus:outline-hidden disabled:opacity-60"
            />
            <button
              onClick={() => send(input)}
              disabled={status === 'sending' || input.trim() === ''}
              aria-label="Send message"
              className="shrink-0 flex h-9 w-9 items-center justify-center rounded-lg bg-brand-primary text-white transition-opacity hover:opacity-90 disabled:opacity-40 focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2"
            >
              <ArrowUp className="w-5 h-5" aria-hidden="true" />
            </button>
          </div>
          <p className="mt-2 text-center text-xs text-text-muted">
            {pod.footerNote}
            {showPrivacyLink && (
              <>
                {' '}
                {pod.privacyUrl ? (
                  // The site's OWN notice (csb): the operator is the controller
                  // there and the notice covers this chat. Opened in a new tab,
                  // so the conversation in this frame is not lost.
                  <a
                    href={pod.privacyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline underline-offset-2 hover:text-brand-primary focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent rounded"
                  >
                    Privacy
                  </a>
                ) : (
                  <Link
                    to="/privacy"
                    className="underline underline-offset-2 hover:text-brand-primary focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent rounded"
                  >
                    Privacy
                  </Link>
                )}
              </>
            )}
          </p>
          {pod.builtByForedesk && (
            <p className="mt-1 text-center text-xs text-text-muted">
              Built by{' '}
              <a
                href="https://www.foredesk.co.uk"
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-2 hover:text-brand-primary focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent rounded"
              >
                Foredesk
              </a>
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

/* ---------- pieces ---------- */

function OxyMark({ small = false }: { small?: boolean }) {
  return (
    <div
      className={cn(
        'shrink-0 rounded-full bg-brand-primary flex items-center justify-center',
        small ? 'w-8 h-8' : 'w-9 h-9',
      )}
      aria-hidden="true"
    >
      <span className={cn('rounded-full border-2 border-brand-accent', small ? 'w-3 h-3' : 'w-3.5 h-3.5')} />
    </div>
  )
}

function Thinking() {
  return (
    <div className="flex items-center gap-1.5 rounded-card border border-border-subtle bg-surface-raised px-4 py-4">
      <span className="oxy-dot h-2 w-2 rounded-full bg-brand-primary" />
      <span className="oxy-dot h-2 w-2 rounded-full bg-brand-primary" />
      <span className="oxy-dot h-2 w-2 rounded-full bg-brand-primary" />
    </div>
  )
}

function EmptyState({
  assistantName,
  tagline,
  welcomeIntro,
  starters,
  onPick,
  disabled,
}: {
  assistantName: string
  tagline: string
  welcomeIntro: string
  starters: string[]
  onPick: (text: string) => void
  disabled: boolean
}) {
  return (
    <div className="py-8">
      <div className="flex items-center gap-3 mb-4">
        <OxyMark />
        <div>
          <p className="font-display text-lg font-semibold tracking-tight">Hi, I'm {assistantName}.</p>
          <p className="text-sm text-text-muted">{tagline}</p>
        </div>
      </div>
      <p className="text-text-muted mb-5 max-w-prose">{welcomeIntro}</p>
      <div className="flex flex-wrap gap-2">
        {starters.map((q) => (
          <button
            key={q}
            onClick={() => onPick(q)}
            disabled={disabled}
            className="rounded-full border border-border-subtle px-3.5 py-1.5 text-sm text-text-primary transition-colors hover:border-brand-accent hover:text-brand-primary disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-brand-accent"
          >
            {q}
          </button>
        ))}
      </div>
    </div>
  )
}

/**
 * THE ORIGINALS BEHIND AN ANSWER (Kelvo step 3c). One button per document —
 * several facts from the same RAMS are one thing to open — shown open, not
 * folded away under "Sources", because on site the file IS the answer as often
 * as the words are. Drawn only when the staff chat supplied `open`.
 */
function OriginalDocuments({
  sources,
  open,
}: {
  sources: Source[]
  open: (documentId: string, name: string) => Promise<void>
}) {
  const docs = openableDocuments(sources)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  if (docs.length === 0) return null

  async function openOne(documentId: string, name: string) {
    setBusy(documentId)
    setError(null)
    try {
      await open(documentId, name)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not open that document.')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="mt-2 pl-1">
      <ul className="flex flex-wrap gap-2">
        {docs.map((d) => (
          <li key={d.documentId}>
            <button
              onClick={() => void openOne(d.documentId, d.name)}
              disabled={busy !== null}
              aria-busy={busy === d.documentId}
              className="flex max-w-full items-center gap-1.5 rounded-lg border border-border-subtle bg-surface-raised px-3 py-1.5 text-left text-xs font-medium text-brand-primary transition-colors hover:border-brand-accent disabled:opacity-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2"
            >
              <FileText className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span className="truncate">
                {busy === d.documentId ? 'Opening…' : `Open ${d.name}`}
              </span>
            </button>
          </li>
        ))}
      </ul>
      {error ? (
        <p role="alert" className="mt-1.5 text-xs text-text-danger">
          {error}
        </p>
      ) : null}
    </div>
  )
}

function Sources({ sources }: { sources: Source[] }) {
  const [open, setOpen] = useState(false)
  // De-duplicate by page for a clean, human-readable provenance line.
  const pages = Array.from(new Set(sources.map((s) => s.page)))
  const byPage = pages.map((page) => ({
    page,
    url: sources.find((s) => s.page === page)?.url ?? '#',
  }))

  return (
    <div className="mt-1.5 pl-1">
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex items-center gap-1 text-xs text-text-muted hover:text-brand-primary focus-visible:ring-2 focus-visible:ring-brand-accent rounded"
      >
        <ChevronDown className={cn('w-3.5 h-3.5 transition-transform', open && '[transform:rotate(180deg)]')} aria-hidden="true" />
        {open ? 'Hide sources' : `Sources · ${pages.length} ${pages.length === 1 ? 'page' : 'pages'}`}
      </button>
      {open && (
        <ul className="mt-1.5 stack-y-1">
          {byPage.map((s) => (
            <li key={s.page}>
              <a
                href={s.url}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-brand-primary underline underline-offset-2 hover:no-underline focus-visible:ring-2 focus-visible:ring-brand-accent rounded"
              >
                {s.page}
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/* ---------- safe, dependency-free message formatting ---------- */

/** Render **bold** spans within a line as real elements (no HTML injection). */
function renderInline(text: string): ReactNode[] {
  return text.split(/\*\*(.+?)\*\*/g).map((part, i) =>
    i % 2 === 1 ? <strong key={i}>{part}</strong> : <span key={i}>{part}</span>,
  )
}

/** Turn the engine's lightly-formatted text into paragraphs and bullet lists. */
function FormattedMessage({ text }: { text: string }) {
  const blocks: ReactNode[] = []
  let bullets: string[] = []

  const flush = () => {
    if (bullets.length === 0) return
    blocks.push(
      <ul key={`ul-${blocks.length}`} className="my-1.5 list-disc pl-5 stack-y-1">
        {bullets.map((b, k) => (
          <li key={k}>{renderInline(b)}</li>
        ))}
      </ul>,
    )
    bullets = []
  }

  for (const raw of text.split('\n')) {
    const line = raw.trimEnd()
    const m = line.match(/^\s*[-*]\s+(.*)$/)
    if (m) {
      bullets.push(m[1] ?? '')
      continue
    }
    flush()
    if (line.trim() === '') continue
    blocks.push(
      <p key={`p-${blocks.length}`} className="leading-relaxed [&:not(:first-child)]:mt-2">
        {renderInline(line)}
      </p>,
    )
  }
  flush()

  return <div className="text-text-primary">{blocks}</div>
}
